import { Alert, Button, Tooltip } from '@billda/design-system';
import { EditIcon } from '@billda/design-system';
import { ReactNode } from 'react';
import { ListGangsResponse } from '../../api/gangsApi';
import { GangCreationStep } from '../../types/gangCreationTypes';
import { GangMemberListItem } from '../molecules/GangMemberListItem';

type GangMember = ListGangsResponse['gangs'][number]['members'][number];

interface GangMemberListSectionProps {
  members: GangMember[];
  itemPrefix: string;
  title: string;
  titleIcon?: ReactNode;
  emptyMessage: string;
  /** Semantic token classes for member avatars (surface + text). */
  avatarClassName: string;
  formatCurrency: (amount: number) => string;
  onEdit?: (step: GangCreationStep) => void;
  editStep: GangCreationStep;
  editTooltip: string;
  testIdPrefix?: string;
  testIdSuffix?: string;
}

export const GangMemberListSection = ({
  members,
  itemPrefix,
  title,
  titleIcon,
  emptyMessage,
  avatarClassName,
  formatCurrency,
  onEdit,
  editStep,
  editTooltip,
  testIdPrefix,
  testIdSuffix,
}: GangMemberListSectionProps) => {
  const hasMembers = members.length > 0;
  const buildTestId = (base: string) =>
    `${testIdPrefix ?? ''}${base}${testIdSuffix ?? ''}`;

  return (
    <div data-testid={buildTestId('section')} className="mb-6">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h6 className="type-h6" data-testid={buildTestId('title')}>
          {titleIcon ? (
            <span className="flex items-center gap-2">
              {titleIcon}
              {title}
            </span>
          ) : (
            title
          )}
        </h6>
        {onEdit ? (
          <Tooltip content={editTooltip}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(editStep)}
              aria-label={editTooltip}
              data-testid={buildTestId('edit-button')}
              className="size-8"
            >
              <EditIcon aria-hidden="true" className="size-4" />
            </Button>
          </Tooltip>
        ) : null}
      </div>

      {hasMembers ? (
        <ul className="m-0 list-none p-0" data-testid={buildTestId('list')}>
          {members.map(member => (
            <GangMemberListItem
              key={member.userId}
              member={member}
              itemPrefix={itemPrefix}
              avatarClassName={avatarClassName}
              formatCurrency={formatCurrency}
            />
          ))}
        </ul>
      ) : (
        <Alert
          severity="info"
          variant="outlined"
          className="mt-1"
          data-testid={buildTestId('empty-message')}
        >
          {emptyMessage}
        </Alert>
      )}
    </div>
  );
};
